<template>
  <div id="horizontal-menu">
    <v-overlay :value="OverlayLoadingFlag">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
    <!-- Menu for admins, national and zone levels -->
    <div class="horizontal-menu-container">
      <!-- Mobile menu toggle button -->
      <button class="mobile-menu-toggle" @click="toggleMobileMenu">
        <v-icon>mdi-menu</v-icon>
      </button>

      <div class="left-section">
        <!-- Role-specific header indicator -->
        <div class="role-indicator">
          <div v-if="RoleId == 175" class="national-indicator">
            National Awards
          </div>
          <div v-else class="zone-indicator">
            {{ ZoneDisplayText }}
          </div>
        </div>

        <!-- Horizontal menu navigation - Desktop -->
        <nav class="horizontal-nav desktop-nav">
          <div
            v-for="section in menuSections"
            :key="section.id"
            class="menu-dropdown"
          >
            <div class="dropdown-trigger">
              <span class="section-title">{{ section.title }}</span>
              <v-icon small> mdi-chevron-down </v-icon>
            </div>

            <div class="dropdown-content">
              <router-link
                v-for="item in section.items"
                :key="`item-${item.id}`"
                :to="item.route"
                class="menu-item"
              >
                <v-icon size="18px">
                  {{ item.icon }}
                </v-icon>
                <span>{{ item.title }}</span>
              </router-link>
            </div>
          </div>
        </nav>
      </div>

      <!-- Mobile menu - shown when toggled -->
      <div class="mobile-menu" :class="{ active: mobileMenuOpen }">
        <div
          v-for="section in menuSections"
          :key="`mobile-${section.id}`"
          class="mobile-dropdown"
        >
          <div class="mobile-section-title" @click="toggleSection(section.id)">
            <span>{{ section.title }}</span>
            <v-icon small>
              {{
                expandedSections.includes(section.id)
                  ? "mdi-chevron-up"
                  : "mdi-chevron-down"
              }}
            </v-icon>
          </div>

          <div
            class="mobile-section-content"
            :class="{ expanded: expandedSections.includes(section.id) }"
          >
            <router-link
              v-for="item in section.items"
              :key="`mobile-item-${item.id}`"
              :to="item.route"
              class="mobile-menu-item"
              @click="closeMobileMenu"
            >
              <v-icon size="18px">
                {{ item.icon }}
              </v-icon>
              <span>{{ item.title }}</span>
            </router-link>
          </div>
        </div>
      </div>

      <!-- User profile section moved from TopBarMenu -->
      <div class="user-section">
        <div class="user-avatar">
          <v-avatar size="40" color="primary lighten-4">
            <img
              v-if="ProfileImagePath"
              :src="ProfileImagePath"
              alt="User Profile"
              class="profile-image"
              @error="handleImageError"
            />
            <span v-else class="initials-text">{{
              getInitials(DisplayName)
            }}</span>
          </v-avatar>
        </div>

        <div class="user-id">
          {{ DisplayName }}
        </div>

        <v-btn elevation="0" class="logout-btn" @click="UserSessionClear">
          LOGOUT
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import common from "@/Common.vue";
import companyConfig from "@/company_config.json";

export default {
  name: "HorizontalMenu",
  components: {}, // Renamed to reflect its new purpose
  mixins: [common],
  data() {
    return {
      RoleId: "",
      Logo: "",
      ZoneDisplayText: "",
      ProfileImagePath: "",
      DisplayName: "",
      OverlayLoadingFlag: false,
      mobileMenuOpen: false,
      expandedSections: [], // Track which sections are expanded in mobile view
      windowWidth: window.innerWidth, // Track window width for responsive behavior

      JudgeFlag: false,
      HeadTableFlag: false,

      // Menu structure with role-based visibility
      menuSections: [],
    };
  },
  methods: {
    getInitials(name) {
      if (!name) return "";
      return name
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase())
        .join("")
        .substring(0, 2);
    },
    handleImageError() {
      console.log("Profile image failed to load, using initials instead");
      // Set ProfileImagePath to null to use the initials instead
      this.ProfileImagePath = null;
    },
    toggleMobileMenu() {
      this.mobileMenuOpen = !this.mobileMenuOpen;
      // When closing the menu, also reset expanded sections
      if (!this.mobileMenuOpen) {
        this.expandedSections = [];
      }
    },
    closeMobileMenu() {
      this.mobileMenuOpen = false;
      this.expandedSections = [];
    },
    toggleSection(sectionId) {
      const index = this.expandedSections.indexOf(sectionId);
      if (index === -1) {
        this.expandedSections.push(sectionId);
      } else {
        this.expandedSections.splice(index, 1);
      }
    },
    handleResize() {
      this.windowWidth = window.innerWidth;
      // Close mobile menu when resizing to desktop view
      if (this.windowWidth > 768 && this.mobileMenuOpen) {
        this.closeMobileMenu();
      }
    },
    fetchMenuData() {
      console.log("Fetching menu data from API");
      this.OverlayLoadingFlag = true;

      console.log("RoleId=" + this.RoleId);
      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;
      var add_url = server_url + "api/awards/menu-lists";
      var upload = {
        UserInterface: 1,
        RoleId: this.RoleId,
      };
      console.log("upload=" + JSON.stringify(upload));
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("Menu API response:", response.data);
          thisIns.OverlayLoadingFlag = false;

          const flag = response.data.flag;

          if (
            flag == 1 &&
            response.data.records &&
            response.data.records.length > 0
          ) {
            thisIns.menuSections = response.data.records;
            console.log("Menu sections loaded from API:", thisIns.menuSections);
          } else {
            console.error("Failed to load menu data or empty response");
          }
        })
        .catch(function (error) {
          console.error("Error fetching menu data:", error);
          thisIns.OverlayLoadingFlag = false;
        });
    },
    UserSessionClear() {
      console.log("UserSessionClear called");

      var role_id = this.$session.get("RoleId");
      role_id = role_id == (null || undefined) ? 0 : role_id;

      if (role_id > 0) {
        this.OverlayLoadingFlag = true;

        var server_url = companyConfig.apiURL;
        var token = this.$session.get("token");
        token = token == (null || undefined) ? 0 : token;
        var add_url = server_url + "api/users/clear-session";
        var upload = {
          UserInterface: 2,
        };
        console.log("upload=" + JSON.stringify(upload));
        console.log(
          "server_url=" +
            server_url +
            ", token=" +
            token +
            ", add_url=" +
            add_url
        );

        const thisIns = this;
        var output = "";
        var records = "";
        var error = "";
        var flag = 0;

        this.$http({
          url: add_url,
          method: "POST",
          data: upload,
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + token,
          },
          onUploadProgress: function (progressEvent) {
            this.uploadPercentage = parseInt(
              Math.round((progressEvent.loaded / progressEvent.total) * 100)
            );
          }.bind(this),
        })
          .then(function (response) {
            console.log("response=" + JSON.stringify(response));

            thisIns.OverlayLoadingFlag = false;

            flag = response.data.flag;
            output = response.data.output;
            error = response.data.error;
            records = response.data.records;
            console.log({ flag }, { output }, { error }, { records });

            if (flag == 1) {
              thisIns.LogoutUser();
            } else {
              if (parseInt(error) == 401) {
                thisIns.LogoutUser();
              }
              thisIns.sweetAlert("error", output, false);
            }
          })
          .catch(function (error) {
            console.log("error=" + error);
            thisIns.OverlayLoadingFlag = false;
          });
      }
    },
  },
  computed: {},
  beforeMount() {
    // Original SidebarMenu initialization
    var roleId = this.$session.get("RoleId");
    roleId = roleId == (null || undefined) ? "" : roleId;
    this.RoleId = roleId;

    var JudgeFlag = roleId == 173 ? true : false;
    console.log({ JudgeFlag });
    this.JudgeFlag = JudgeFlag;
    this.HeadTableFlag = JudgeFlag ? false : true;

    var zoneNameTxt = this.$session.get("ZoneDisplayText");
    zoneNameTxt = zoneNameTxt == (null || undefined) ? "" : zoneNameTxt;
    console.log({ zoneNameTxt });
    this.ZoneDisplayText = zoneNameTxt;

    var server_url = companyConfig.apiURL;
    this.Logo = server_url + "assets/images/logo.png";

    // Moved from TopBarMenu
    console.log("Loading user profile");
    var user = this.$session.get("user");
    console.log({ user });
    user = user == (null || undefined) ? {} : user;
    console.log({ user });

    var DisplayName = Object.prototype.hasOwnProperty.call(user, "Username")
      ? user.Username
      : "";
    console.log({ DisplayName });
    this.DisplayName = DisplayName;

    var MemberImage = Object.prototype.hasOwnProperty.call(user, "MemberImage")
      ? user.MemberImage
      : "";
    console.log({ MemberImage });

    // Check if MemberImage is a valid URL or path
    if (MemberImage && MemberImage.trim() !== "") {
      // If it doesn't have http or https, prepend the API URL
      if (
        !MemberImage.startsWith("http://") &&
        !MemberImage.startsWith("https://")
      ) {
        this.ProfileImagePath =
          companyConfig.apiURL + MemberImage.replace(/^\//, "");
      } else {
        this.ProfileImagePath = MemberImage;
      }
    } else {
      this.ProfileImagePath =
        companyConfig.apiURL + "assets/images/default-profile.png";
    }

    // Fetch menu data from API
    this.fetchMenuData();
  },
  mounted() {
    // Add event listener for window resize
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    // Remove event listener when component is destroyed
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style lang="scss" scoped>
#horizontal-menu {
  width: 100%;
  position: relative;
  z-index: 10;

  .v-overlay {
    .v-progress-circular {
      color: #0e88d3;
    }
  }

  .horizontal-menu-container {
    background-color: #0e88d3; // Blue from screenshot
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    width: 100%;
    position: relative;
    overflow: visible;
  }

  // Mobile menu toggle button - hidden on desktop
  .mobile-menu-toggle {
    display: none;
    background: none;
    border: none;
    color: white;
    padding: 8px;
    cursor: pointer;

    .v-icon {
      font-size: 24px;
    }

    @media (max-width: 768px) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .left-section {
    display: flex;
    align-items: center;
    height: 100%;
    flex: 1;

    @media (max-width: 768px) {
      justify-content: center;
    }
  }

  .role-indicator {
    display: flex;
    align-items: center;
    margin-right: 30px;
    padding: 4px 0;

    .zone-indicator,
    .national-indicator,
    .judge-indicator {
      color: white;
      font-weight: 600;
      font-size: 14px;
      letter-spacing: 0.5px;
      padding: 4px 10px;
    }

    @media (max-width: 768px) {
      margin-right: 0;
    }
  }

  // Desktop horizontal navigation
  .horizontal-nav {
    display: flex;
    align-items: center;
    height: 100%;

    &.desktop-nav {
      @media (max-width: 768px) {
        display: none; // Hide desktop menu on mobile
      }
    }

    .menu-dropdown {
      position: relative;
      height: 100%;
      display: flex;
      align-items: center;
      margin-right: 5px;

      &:hover {
        .dropdown-content {
          display: block;
        }
      }

      .dropdown-trigger {
        display: flex;
        align-items: center;
        padding: 0 15px;
        height: 100%;
        cursor: pointer;
        color: white;
        transition: background-color 0.2s;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1);
        }

        .section-title {
          font-size: 14px;
          font-weight: 500;
          margin-right: 5px;
        }
      }

      .dropdown-content {
        display: none;
        position: absolute;
        top: 100%;
        left: 0;
        background-color: white;
        min-width: 220px;
        box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
        border-radius: 0 0 4px 4px;
        z-index: 100;
        padding: 5px 0;

        .menu-item {
          display: flex;
          align-items: center;
          padding: 10px 15px;
          text-decoration: none;
          color: #3a3b45;
          transition: all 0.2s;

          &:hover {
            background-color: #f8f9fc;
            color: #0e88d3;
          }

          &.router-link-exact-active {
            color: #0e88d3;
            background-color: #f0f4f9;
            font-weight: 500;
          }

          .v-icon {
            color: #5a5c69;
            margin-right: 10px;
            transition: all 0.2s;
          }

          &:hover .v-icon,
          &.router-link-exact-active .v-icon {
            color: #0e88d3;
          }
        }
      }
    }
  }

  // Mobile menu styles
  .mobile-menu {
    display: none;
    position: absolute;
    top: 56px;
    left: 0;
    right: 0;
    background-color: #0e88d3;
    z-index: 1000;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-out;

    &.active {
      display: block;
      max-height: 80vh;
      overflow-y: auto;
    }

    .mobile-dropdown {
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);

      .mobile-section-title {
        padding: 15px 20px;
        color: white;
        font-weight: 500;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;

        &:hover {
          background-color: rgba(255, 255, 255, 0.05);
        }
      }

      .mobile-section-content {
        max-height: 0;
        overflow: hidden;
        transition: max-height 0.2s ease-out;
        background-color: rgba(0, 0, 0, 0.1);

        &.expanded {
          max-height: 500px;
        }

        .mobile-menu-item {
          display: flex;
          align-items: center;
          padding: 12px 20px 12px 40px;
          text-decoration: none;
          color: white;

          &:hover {
            background-color: rgba(255, 255, 255, 0.1);
          }

          &.router-link-exact-active {
            background-color: rgba(255, 255, 255, 0.15);
            font-weight: 500;
          }

          .v-icon {
            margin-right: 10px;
            color: rgba(255, 255, 255, 0.8);
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .horizontal-menu-container {
      padding: 0 16px;
    }
  }
  .judge-menu {
    background-color: #0e88d3; // Match the same color for judge menu
  }

  // User section styles moved from TopBarMenu
  .user-section {
    display: flex;
    align-items: center;
    height: 100%;

    .user-avatar {
      display: flex;
      align-items: center;
      justify-content: center;

      .v-avatar {
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
        overflow: hidden;
        background-color: #f0f8ff;

        .profile-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }

        .initials-text {
          font-size: 16px;
          font-weight: 600;
          color: #0e88d3;
          letter-spacing: 0.5px;
        }
      }
    }

    .user-id {
      margin: 0 16px;
      color: white;
      font-weight: 500;
      font-size: 16px;
      letter-spacing: 0.5px;
    }

    .logout-btn {
      background-color: rgba(255, 255, 255, 0.2) !important;
      color: white !important;
      font-weight: 500 !important;
      letter-spacing: 0.8px !important;
      border-radius: 4px !important;
      padding: 0 20px !important;
      height: 36px !important;
      transition: all 0.2s ease;

      &:hover {
        background-color: rgba(255, 255, 255, 0.3) !important;
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1) !important;
      }

      &:active {
        transform: translateY(1px);
      }
    }
  }
}
</style>